// Builds the scanner test barcodes from the department's own items and lots,
// so every code scans to a real (or deliberately missing) record.

const UNKNOWN_SKU_PREFIX = 'UKENDT-';
const UNKNOWN_LOT_PREFIX = 'NYT-BATCH-';

// GS1 mod-10 check digit for the digits before it
export function gs1CheckDigit(digits) {
  const sum = [...digits].reverse().reduce((acc, d, i) => acc + Number(d) * (i % 2 === 0 ? 3 : 1), 0);
  return String((10 - (sum % 10)) % 10);
}

const isEan13 = code => /^\d{13}$/.test(code ?? '');

// "2028-07-11" → "280711"
const yymmdd = date => date.slice(2).replaceAll('-', '');

/**
 * A GS1 DataMatrix: GTIN, then expiry, then lot. The lot goes last because it
 * is variable-length; last, it needs no separator after it.
 * `value` is what a scanner types, `text` is the bracketed form bwip-js draws.
 */
export function gs1Code({ ean, expiry, lot }) {
  const parts = [['01', `0${ean}`]];
  if (expiry) parts.push(['17', yymmdd(expiry)]);
  if (lot) parts.push(['10', lot]);
  return {
    symbology: 'gs1datamatrix',
    value: parts.map(([ai, v]) => ai + v).join(''),
    text: parts.map(([ai, v]) => `(${ai})${v}`).join(''),
  };
}

const eanCode = ean => ({ symbology: 'ean13', value: ean, text: ean });
const dataMatrix = value => ({ symbology: 'datamatrix', value, text: value });

function lastYearJan1(today) {
  return `${Number(today.slice(0, 4)) - 1}-01-01`;
}

/**
 * @param items  item list rows (with supplier_items[].barcode)
 * @param groups product groups (lot_tracked, hazardous)
 * @param lots   lot filter rows (with item.item_id)
 * @param today  "YYYY-MM-DD"
 * @returns [{ key, item?, lot?, symbology, value, text, params? }] — key picks the
 *          title and expected result from inventory:test_barcodes.cases.<key>
 */
export function buildTestCodes({ items, groups, lots, today }) {
  const groupOf = item => groups.find(g => g.product_group_id === item.product_group_id) ?? {};
  const eanOf = item => item.supplier_items?.map(s => s.barcode).find(isEan13);
  const itemOf = lot => items.find(i => i.item_id === lot.item?.item_id);
  const scannable = items.filter(i => i.active && eanOf(i));

  const codes = [];
  const add = (key, code, extra = {}) => codes.push({ key, ...code, ...extra });

  const plain = scannable.find(i => !groupOf(i).lot_tracked && !groupOf(i).hazardous);
  if (plain) add('ean_plain', eanCode(eanOf(plain)), { item: plain });

  const hazardous = scannable.find(i => groupOf(i).hazardous);
  if (hazardous) add('ean_hazardous', eanCode(eanOf(hazardous)), { item: hazardous });

  if (plain) add('sku', dataMatrix(plain.sku), { item: plain });

  // Lots whose item has a barcode, soonest expiry first
  const lotRows = lots
    .map(lot => ({ lot, item: itemOf(lot) }))
    .filter(({ item }) => item && item.active && eanOf(item) && groupOf(item).lot_tracked)
    .sort((a, b) => (a.lot.expiry_date || '9999').localeCompare(b.lot.expiry_date || '9999'));

  const valid = lotRows.find(({ lot }) => lot.status === 'active' && lot.expiry_date && lot.expiry_date >= today);
  if (valid) {
    add('gs1_valid', gs1Code({ ean: eanOf(valid.item), expiry: valid.lot.expiry_date, lot: valid.lot.lot_number }), valid);
  }

  // A lot that really is expired, else a real lot with a past date in the code
  const expired = lotRows.find(({ lot }) => lot.expiry_date && lot.expiry_date < today);
  if (expired) {
    add('gs1_expired', gs1Code({ ean: eanOf(expired.item), expiry: expired.lot.expiry_date, lot: expired.lot.lot_number }), expired);
  } else if (valid) {
    const expiry = lastYearJan1(today);
    add('gs1_expired_label', gs1Code({ ean: eanOf(valid.item), expiry, lot: valid.lot.lot_number }), { ...valid, params: { date: expiry } });
  }

  const blocked = lotRows.find(({ lot }) => ['recalled', 'quarantined'].includes(lot.status));
  if (blocked) add('gs1_blocked', gs1Code({ ean: eanOf(blocked.item), expiry: blocked.lot.expiry_date, lot: blocked.lot.lot_number }), blocked);

  // Next free lot number, so the code stays "new" after someone creates it in a demo
  const newLotFor = (valid ?? lotRows[0])?.item;
  if (newLotFor) {
    const existing = new Set(lots.filter(l => l.item?.item_id === newLotFor.item_id).map(l => l.lot_number.toUpperCase()));
    let n = 1;
    while (existing.has(`${UNKNOWN_LOT_PREFIX}${String(n).padStart(2, '0')}`)) n += 1;
    const lot = `${UNKNOWN_LOT_PREFIX}${String(n).padStart(2, '0')}`;
    const expiry = `${Number(today.slice(0, 4)) + 2}-12-31`;
    add('gs1_unknown_lot', gs1Code({ ean: eanOf(newLotFor), expiry, lot }), { item: newLotFor, params: { lot } });
  }

  const inactive = items.find(i => !i.active && eanOf(i));
  if (inactive) add('ean_inactive', eanCode(eanOf(inactive)), { item: inactive });

  // Codes nobody has. Bumped until free, in case a demo created the last one
  const taken = new Set(items.flatMap(i => i.supplier_items?.map(s => s.barcode) ?? []));
  let base = 570999999999;
  let unknownEan;
  do {
    const digits = String(base);
    unknownEan = digits + gs1CheckDigit(digits);
    base -= 1;
  } while (taken.has(unknownEan));
  add('ean_unknown', eanCode(unknownEan));

  const skus = new Set(items.map(i => i.sku.toUpperCase()));
  let s = 0;
  while (skus.has(`${UNKNOWN_SKU_PREFIX}${String(s).padStart(4, '0')}`)) s += 1;
  add('sku_unknown', dataMatrix(`${UNKNOWN_SKU_PREFIX}${String(s).padStart(4, '0')}`));

  return codes;
}
